import React, { useEffect, useState } from "react";
import axios from "axios";
import { useForm } from "react-hook-form";
import { useOutletContext, useNavigate } from "react-router-dom";
import $ from "jquery";
import toastr from "../../../utils/toast";

const normalizeMultiValue = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    return value.map(String);
  }

  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
};

function EditNoPoach({ id, onClose }) {
  const { setGlobalLoading, token } = useOutletContext();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm();

  const [contract, setContract] = useState({});

  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);

  const [noPoachClause, setNoPoachClause] = useState("no");

  const [selectedCountry, setSelectedCountry] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState([]);

  const [loadingContract, setLoadingContract] = useState(true);
  const [loadingLocations, setLoadingLocations] = useState(false);

  /*
   * ---------------------------------------------------------
   * Fetch contract details
   * ---------------------------------------------------------
   *
   * Client name now comes directly from get_contract_details
   */
  useEffect(() => {
    const fetchContractDetails = async () => {
      try {
        setLoadingContract(true);
        setGlobalLoading(true);

        const response = await axios.get(
          `${API_BASE_URL}/contracts/get_contract_details/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data =
          response.data.ref_list?.[0]?.data || {};

        console.log("Contract:", data);

        setContract(data);

        /*
         * Read-only fields
         */
        setValue(
          "contract_name",
          data.contract_name || ""
        );

        setValue(
          "company_id",
          data.company_id || ""
        );

        /*
         * No Poach
         */
        const clause =
          data.noPoachClause || "no";

        setNoPoachClause(clause);

        setValue(
          "noPoachClause",
          clause
        );

        /*
         * Country
         */
        if (clause === "country") {
          const values = normalizeMultiValue(
            data.noPoachClauseValue
          );

          setSelectedCountry(values);

          setValue(
            "country_id",
            values
          );

          setValue(
            "noPoachEndDateCountry",
            data.noPoachEndDate || ""
          );
        }

        /*
         * Region
         */
        if (clause === "region") {
          const values = normalizeMultiValue(
            data.noPoachClauseValue
          );

          setSelectedRegion(values);

          setValue(
            "region_id",
            values
          );

          setValue(
            "noPoachEndDateRegion",
            data.noPoachEndDate || ""
          );
        }
      } catch (error) {
        console.error(
          "Error fetching contract details:",
          error
        );

        toastr.error(
          "Unable to load contract details."
        );
      } finally {
        setLoadingContract(false);
        setGlobalLoading(false);
      }
    };

    fetchContractDetails();
  }, [
    id,
    API_BASE_URL,
    token,
    setGlobalLoading,
    setValue,
  ]);

  /*
   * ---------------------------------------------------------
   * Fetch only countries / regions
   * ---------------------------------------------------------
   *
   * Companies are NO LONGER fetched.
   */
  useEffect(() => {
    if (
      !noPoachClause ||
      noPoachClause === "no"
    ) {
      return;
    }

    const fetchLocationData = async () => {
      try {
        setLoadingLocations(true);

        const response = await axios.get(
          `${API_BASE_URL}/contracts/contractd`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (noPoachClause === "country") {
          setCountries(
            response.data.countries || []
          );
        }

        if (noPoachClause === "region") {
          setRegions(
            response.data.regions || []
          );
        }
      } catch (error) {
        console.error(
          "Error fetching location data:",
          error
        );

        toastr.error(
          "Unable to load location data."
        );
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocationData();
  }, [
    noPoachClause,
    API_BASE_URL,
    token,
  ]);

  /*
   * ---------------------------------------------------------
   * Initialize Country Select2
   * ---------------------------------------------------------
   */
  useEffect(() => {
    if (
      noPoachClause !== "country" ||
      !countries.length
    ) {
      return;
    }

    const $select = $(
      "[name='country_id']"
    );

    /*
     * Destroy existing Select2
     */
    if (
      $select.hasClass(
        "select2-hidden-accessible"
      )
    ) {
      $select.select2("destroy");
    }

    /*
     * Initialize
     */
    $select.select2({
      placeholder: "Please Select Countries",
      width: "100%",
    });

    /*
     * Set existing selected values
     */
    const values = normalizeMultiValue(
      selectedCountry
    );

    $select
      .val(values)
      .trigger("change.select2");

    /*
     * Change event
     */
    const handleChange = function () {
      const value =
        $(this).val() || [];

      setValue(
        "country_id",
        value
      );

      setSelectedCountry(value);
    };

    $select.on(
      "change",
      handleChange
    );

    /*
     * Cleanup
     */
    return () => {
      $select.off(
        "change",
        handleChange
      );

      if (
        $select.hasClass(
          "select2-hidden-accessible"
        )
      ) {
        $select.select2("destroy");
      }
    };
  }, [
    noPoachClause,
    countries,
    setValue,
  ]);

  /*
   * ---------------------------------------------------------
   * Initialize Region Select2
   * ---------------------------------------------------------
   */
  useEffect(() => {
    if (
      noPoachClause !== "region" ||
      !regions.length
    ) {
      return;
    }

    const $select = $(
      "[name='region_id']"
    );

    /*
     * Destroy existing Select2
     */
    if (
      $select.hasClass(
        "select2-hidden-accessible"
      )
    ) {
      $select.select2("destroy");
    }

    /*
     * Initialize
     */
    $select.select2({
      placeholder: "Please Select Regions",
      width: "100%",
    });

    /*
     * Set existing selected values
     */
    const values = normalizeMultiValue(
      selectedRegion
    );

    $select
      .val(values)
      .trigger("change.select2");

    /*
     * Change event
     */
    const handleChange = function () {
      const value =
        $(this).val() || [];

      setValue(
        "region_id",
        value
      );

      setSelectedRegion(value);
    };

    $select.on(
      "change",
      handleChange
    );

    /*
     * Cleanup
     */
    return () => {
      $select.off(
        "change",
        handleChange
      );

      if (
        $select.hasClass(
          "select2-hidden-accessible"
        )
      ) {
        $select.select2("destroy");
      }
    };
  }, [
    noPoachClause,
    regions,
    setValue,
  ]);

  /*
   * ---------------------------------------------------------
   * No Poach change
   * ---------------------------------------------------------
   */
  const handleNoPoachChange = (
    value
  ) => {
    setNoPoachClause(value);

    setSelectedCountry([]);
    setSelectedRegion([]);
    setCountries([]);
    setRegions([]);
    setValue("country_id", []);
    setValue("region_id", []);
    setValue("noPoachEndDateCountry", "");
    setValue("noPoachEndDateRegion",  "");
  };

const onSubmit = async (formDataValues) => {
  if (noPoachClause === "region" && (!selectedRegion || selectedRegion.length === 0)) {
    const $select = $("select[name='region_id']");
    if ($select.hasClass("select2-hidden-accessible")) {
      $select.select2("open");
    }
    return;
  }

  if (noPoachClause === "country" && (!selectedCountry || selectedCountry.length === 0)) {
    const $select = $("select[name='country_id']");
    if ($select.hasClass("select2-hidden-accessible")) {
      $select.select2("open");
    }
    return;
  }

  const payload = {
    id: Number(id),
    noPoachClause: noPoachClause || "no",
    country_id: noPoachClause === "country" ? selectedCountry : [],
    region_id: noPoachClause === "region" ? selectedRegion : [],
    noPoachEndDateCountry: noPoachClause === "country" ? formDataValues.noPoachEndDateCountry || "" : "",
    noPoachEndDateRegion: noPoachClause === "region" ? formDataValues.noPoachEndDateRegion || "" : "",
  };

  console.log("No Poach Payload:", payload);

  try {
    setGlobalLoading(true);

    const response = await axios.post(
      `${API_BASE_URL}/contracts/update_contract_nopoach`,
      payload,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );
    console.log("No Poach Update Response:", response.data);
    toastr.success("No Poach details updated successfully!");

    onClose();

    setTimeout(() => {
      navigate("/business_intelligence/nopoachList");
    }, 500);

  } catch (error) {
    console.error(
      "No Poach update failed:",
      error.response?.data || error
    );

    toastr.error(
      error.response?.data?.message ||
      "Error updating No Poach details."
    );
  } finally {
    setGlobalLoading(false);
  }
};

  return (
    <div className="modal fade show d-block" tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered modal-md" role="document" style={{ maxWidth: "950px"}}>
        <div className="modal-content">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="modal-header">
              <h4 className="modal-title">
                Edit No Poach
              </h4>
              <button onClick={onClose} type="button" className="btn-close custom-btn-close" aria-label="Close">
                <i className="ti ti-x"></i>
              </button>
            </div>

            <div className="modal-body" style={{textAlign: "left"}}>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Contract Name
                  </label>
                  <input type="text" {...register("contract_name")} className="form-control" readOnly value={contract.contract_name || ""} />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Client Name
                  </label>
                  <input type="text" className="form-control" readOnly value={contract.company_name || ""} />
                  <input type="hidden" {...register("company_id")} />
                </div>
              </div>

              {/* No Poach */}
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">
                    No Poach Clause *
                  </label>

                  <select {...register("noPoachClause", {required:"Required"})} value={noPoachClause}
                    onChange={(e) => handleNoPoachChange(e.target.value)} className="form-select">
                    <option value="no">No</option>
                    <option value="country">Country</option>
                    <option value="region">Region</option>
                  </select>
                </div>

                {/* Country */}
                {noPoachClause ===
                  "country" && (
                  <>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        No Poach Country *
                      </label>

                      <select {...register("country_id", {required: "Required"})} multiple className="form-select select2">
                        {countries.map(
                          (country) => (
                            <option key={country.id} value={country.id}>{country.name}</option>
                          )
                        )}
                      </select>

                      {loadingLocations && (<small className="text-muted">Loading countries...</small>)}
                      {errors.country_id && (
                        <div className="text-danger mt-1">
                          {errors.country_id.message}
                        </div>
                      )}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        No Poach End Date *
                      </label>
                      <input {...register("noPoachEndDateCountry", {required: "Required"})} type="date" className="form-control" />
                    </div>
                  </>
                )}

                {/* Region */}
                {noPoachClause ===
                  "region" && (
                  <>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        No Poach Region *
                      </label>

                      <select {...register("region_id", {required: "Required"})} multiple className="form-select select2">
                        {regions.map(
                          (region) => (
                            <option key={region.id} value={region.id}>
                              {region.name}
                            </option>
                          )
                        )}
                      </select>

                      {loadingLocations && (
                        <small className="text-muted">
                          Loading regions...
                        </small>
                      )}

                      {errors.region_id && (
                        <div className="text-danger mt-1">
                          {
                            errors
                              .region_id
                              .message
                          }
                        </div>
                      )}
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">
                        No Poach End Date *
                      </label>

                      <input
                        {...register(
                          "noPoachEndDateRegion",
                          {
                            required:
                              "Required",
                          }
                        )}
                        type="date"
                        className="form-control"
                      />
                    </div>
                  </>
                )}

              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">

              <button
                onClick={onClose}
                type="button"
                className="btn btn-secondary"
              >
                Close
              </button>

              <button
                type="submit"
                className="btn btn-primary ms-2"
                disabled={
                  loadingContract
                }
              >
                Update
              </button>

            </div>

          </form>
        </div>
      </div>
    </div>
  );
}

export default EditNoPoach;